import { useEffect, useRef } from 'react'
import type { LegalDocument } from '@/shared/domain'
import type { LoadCurrentDocument } from '@/shared/domain'
import { Checkbox } from './checkbox'
import { Label } from './label'

export type LegalCheckboxesProps = {
  tosAccepted: boolean
  privacyAccepted: boolean
  onTosChange: (value: boolean) => void
  onPrivacyChange: (value: boolean) => void
  tosUrl?: string
  privacyUrl?: string
  onLoadCurrentDocument?: LoadCurrentDocument
  onDocumentsLoaded?: (documents: LegalDocument[]) => void
}

const DEFAULT_TOS_URL = '/terms-of-service'
const DEFAULT_PRIVACY_URL = '/privacy-policy'

export function LegalCheckboxes({
  tosAccepted,
  privacyAccepted,
  onTosChange,
  onPrivacyChange,
  tosUrl = DEFAULT_TOS_URL,
  privacyUrl = DEFAULT_PRIVACY_URL,
  onLoadCurrentDocument,
  onDocumentsLoaded,
}: LegalCheckboxesProps) {
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (!onLoadCurrentDocument || !onDocumentsLoaded) return
    if (fetchedRef.current) return
    fetchedRef.current = true
    let cancelled = false
    Promise.all([
      onLoadCurrentDocument({ documentType: 'terms_of_service' }),
      onLoadCurrentDocument({ documentType: 'privacy_policy' }),
    ])
      .then((results) => {
        if (cancelled) return
        const documents = results.filter((doc): doc is LegalDocument => doc !== null)
        onDocumentsLoaded(documents)
      })
      .catch(() => {
        if (cancelled) return
      })
    return () => {
      cancelled = true
    }
  }, [onLoadCurrentDocument, onDocumentsLoaded])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id="legal-tos"
          aria-label="I agree to the Terms of Service"
          checked={tosAccepted}
          onCheckedChange={(value) => onTosChange(value === true)}
        />
        <Label htmlFor="legal-tos" className="text-sm font-normal leading-snug">
          <span>
            I agree to the{' '}
            <a
              href={tosUrl}
              className="font-medium text-foreground underline hover:no-underline"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
          </span>
        </Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="legal-privacy"
          aria-label="I agree to the Privacy Policy"
          checked={privacyAccepted}
          onCheckedChange={(value) => onPrivacyChange(value === true)}
        />
        <Label htmlFor="legal-privacy" className="text-sm font-normal leading-snug">
          <span>
            I agree to the{' '}
            <a
              href={privacyUrl}
              className="font-medium text-foreground underline hover:no-underline"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
          </span>
        </Label>
      </div>
    </div>
  )
}
